import { useState } from "react";
import { CopyLandingPageButton } from "@app/components/copy-landing-page-button";
import { ROLE_LABELS } from "@app/lib/role-constants";
import {
  canViewPublicEventLandingPage,
  getEventStatusDisplay,
  normalizeEventStatus,
} from "@app/lib/event-status";
import { PlusIcon, SearchIcon } from "@app/lib/events-admin-icons";
import { formatShortDate } from "@app/lib/events-admin-utils";
import { getRoleLabelFromJwtRoles } from "@app/lib/role-utils";
import { SkeletonTableRow } from "@app/components/skeleton";
import { NotAuthorizedState } from "@app/components/auth-guard/not-authorized-state";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import { useServerEventsPage } from "@app/hooks/use-server-events-page";
import { LIST_ORGANIZER_EVENTS_PAGE_INDEX } from "@app/graphql/events.queries";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import { useNavigate } from "react-router";
import {
  Button,
  PaginationPageDefault,
} from "@its-engineering-and-development/ui-foundation/components";
import "./manage-events-page.css";
import { formatOneworld } from "@app/components/oneworld-word";

type ApiEvent = {
  eventId: string;
  eventName: string;
  estimatedTravelerCount: number | null;
  destination: string;
  startDate: string;
  endDate: string;
  status: string;
};

type EventRow = {
  id: string;
  eventName: string;
  guestCount: number | null;
  destination: string;
  startDate: string;
  endDate: string;
  status: string;
};

function getStatusKey(status: string) {
  return status.trim().toUpperCase();
}

function getDisplayStatus(status: string) {
  const normalizedStatus =
    normalizeEventStatus(getStatusKey(status)) ?? getStatusKey(status);
  const display = getEventStatusDisplay(normalizedStatus);

  return {
    label: display.label,
    toneClass: `events-tone events-tone--${display.tone}`,
  };
}

const PAGE_SIZE = 10;

function mapEventRow(eventItem: ApiEvent): EventRow {
  return {
    id: eventItem.eventId,
    eventName: eventItem.eventName,
    guestCount: eventItem.estimatedTravelerCount ?? null,
    destination: eventItem.destination,
    startDate: eventItem.startDate,
    endDate: eventItem.endDate,
    status: eventItem.status,
  };
}

export function ManageEventsPage() {
  const [selectedTab, setSelectedTab] = useState<"active" | "completed">(
    "active",
  );
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const { user } = useJwtAuth();
  const roleLabel = getRoleLabelFromJwtRoles(user?.roles);
  const [searchQuery, setSearchQuery] = useState("");
  const trimmedSearchQuery = searchQuery.trim();

  // Pagination and search are resolved server-side. The "completed" tab passes
  // `state: COMPLETED`; the "active" tab passes no state and lists everything
  // else the backend returns for the page.
  const {
    items: events,
    page,
    totalPages,
    isLoading,
    error,
    retry,
    setPage,
  } = useServerEventsPage<ApiEvent, EventRow>({
    query: LIST_ORGANIZER_EVENTS_PAGE_INDEX,
    organizationId,
    pageSize: PAGE_SIZE,
    search: searchQuery,
    enabled: Boolean(user),
    variables: {
      organizerUserId: roleLabel === ROLE_LABELS.ADMIN ? undefined : user?.id,
      state: selectedTab === "completed" ? "COMPLETED" : undefined,
    },
    mapItem: mapEventRow,
  });

  const showSkeleton = events.length === 0 && isLoading;

  const handleTabChange = (tab: "active" | "completed") => {
    setSelectedTab(tab);
    setPage(1);
  };

  if (!user) {
    return <NotAuthorizedState />;
  }

  return (
    <section className="events-page events-page--stack manage-events-page">
      <header className="events-page__hero">
        <div>
          <h1 className="events-page__title">Manage Events</h1>
          <p className="events-page__subtitle">
            {formatOneworld(
              "View and manage your oneworld events, including invitations, event readiness, and booking completion.",
            )}
          </p>
        </div>

        <Button
          onClick={() => navigate("/")}
          size="lg"
          color="primary"
          className="events-action-button"
        >
          <PlusIcon className="size-4" />
          Create Event
        </Button>
      </header>

      <div
        className="events-tabs"
        role="tablist"
        aria-label="Manage events tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={selectedTab === "active"}
          className={`events-tab ${selectedTab === "active" ? "is-active" : ""}`}
          onClick={() => handleTabChange("active")}
        >
          Active Events
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={selectedTab === "completed"}
          className={`events-tab ${selectedTab === "completed" ? "is-active" : ""}`}
          onClick={() => handleTabChange("completed")}
        >
          Completed Events
        </button>
      </div>

      <div className="events-admin-toolbar">
        <div className="events-admin-toolbar__search">
          <SearchIcon className="size-4" />
          <input
            type="text"
            placeholder="Search Event Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search events"
          />
        </div>
      </div>

      <article className="events-surface events-surface--table">
        <table className="events-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Event Name</th>
              <th>Guest Count</th>
              <th>Destination</th>
              <th>Departure</th>
              <th>Return</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="events-stagger">
            {showSkeleton ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonTableRow key={`skeleton-${idx}`} columns={8} />
              ))
            ) : (
              <>
                {error ? (
                  <tr>
                    <td colSpan={8} className="events-table__empty">
                      <div role="alert">
                        {events.length > 0
                          ? "Unable to refresh events. Showing the latest available results."
                          : "Unable to load events. Check your connection and try again."}
                      </div>
                      <Button
                        size="sm"
                        color="secondary"
                        className="events-outline-button"
                        onPress={retry}
                      >
                        Try again
                      </Button>
                    </td>
                  </tr>
                ) : null}
                {events.length === 0 && !error ? (
                  <tr>
                    <td colSpan={8} className="events-table__empty">
                      {trimmedSearchQuery
                        ? `No events match "${trimmedSearchQuery}".`
                        : `No ${selectedTab === "completed" ? "Completed" : "Active"} events found in this organization.`}
                    </td>
                  </tr>
                ) : (
                  events.map((eventItem) => (
                    <tr
                      key={eventItem.id}
                      className="events-table__row--clickable"
                      onClick={() => navigate(`/events/${eventItem.id}`)}
                    >
                      <td data-label="ID">
                        <span className="events-table__id" title={eventItem.id}>
                          {eventItem.id}
                        </span>
                      </td>
                      <td data-label="Event Name">
                        <span
                          className="events-table__primary-link"
                          title={eventItem.eventName}
                        >
                          {eventItem.eventName}
                        </span>
                      </td>
                      <td data-label="Guest Count">
                        <strong className="events-table__metric">
                          {eventItem.guestCount ?? "—"}
                        </strong>
                      </td>
                      <td data-label="Destination">{eventItem.destination}</td>
                      <td data-label="Departure">
                        {formatShortDate(eventItem.startDate)}
                      </td>
                      <td data-label="Return">
                        {formatShortDate(eventItem.endDate)}
                      </td>
                      <td data-label="Status">
                        {(() => {
                          const display = getDisplayStatus(eventItem.status);
                          return (
                            <span className={display.toneClass}>
                              {display.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td data-label="Actions">
                        <div className="events-table__actions-cell">
                          {canViewPublicEventLandingPage(eventItem.status) ? (
                            <CopyLandingPageButton
                              eventId={eventItem.id}
                              className="events-table__copy-action"
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          ) : null}
                          <button
                            type="button"
                            className="events-table__chevron"
                            aria-label={`Open ${eventItem.eventName}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/events/${eventItem.id}`);
                            }}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="m9 6 6 6-6 6" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
        {!showSkeleton && totalPages > 1 ? (
          <PaginationPageDefault
            page={page}
            total={totalPages}
            onPageChange={setPage}
            className="manage-events-page__pagination"
          />
        ) : null}
      </article>
    </section>
  );
}
